import axios from 'axios'
import type { AxiosError } from 'axios'
import i18n from '../i18n'
import { getAccessToken, logout, refreshAccessToken, setAccessToken } from './auth'
import { endpoints } from './endpoints'
import { formatCnpj, formatDocument } from './formatters'

export const api = axios.create({ baseURL: import.meta.env.VITE_API_BASE_URL })

let refreshPromise: Promise<string | null> | null = null

api.interceptors.request.use(config => {
  const token = getAccessToken()
  config.headers = config.headers ?? {}
  config.headers['Accept-Language'] = i18n.language || 'pt-br'
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const original = error.config as (typeof error.config & { _retry?: boolean })
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true
      refreshPromise ??= refreshAccessToken().finally(() => { refreshPromise = null })
      const nextToken = await refreshPromise
      if (nextToken) {
        setAccessToken(nextToken)
        original.headers = original.headers ?? {}
        original.headers.Authorization = `Bearer ${nextToken}`
        return api(original)
      }
      logout()
    }
    return Promise.reject(error)
  },
)

export interface Invoice {
  id: string
  company: string
  company_name: string
  company_cnpj: string
  key: string
  number: string
  series: string
  issuer_name: string
  issue_date: string
  total_value: string
  recipient_name: string
  recipient_document: string
  recipient_address_street: string
  recipient_address_number: string
  recipient_address_neighborhood: string
  recipient_address_city: string
  recipient_address_uf: string
  recipient_address_zip_code: string
  xml_file?: string | null
  pdf_file?: string | null
  danfe_generation_status?: 'not_generated' | 'pending' | 'processing' | 'available' | 'failed'
  status: string
  created_at: string
  updated_at: string
}

export interface DeliveryAddress {
  street: string
  number: string
  neighborhood: string
  city: string
  uf: string
  zip_code: string
  complement?: string
  reference?: string
}

export interface DeliveryParty {
  name: string
  document: string
  address_street: string
  address_number: string
  address_complement: string
  address_neighborhood: string
  address_city: string
  address_uf: string
  address_zip_code: string
}

export interface DeliveryRecipient extends DeliveryParty { phone: string; email: string }
export type DeliveryIssuer = DeliveryParty
export interface DeliveryItem {
  id?: string
  position: number
  product_code: string
  description: string
  quantity: string
  unit: string
  additional_information: string
  delivery_quantity: string
  delivery_unit: string
  observations: string
  delivered_quantity?: string
  missing_quantity?: string
}

export interface DeliveryPendency {
  id: string
  item_id: string
  quantity: string
  unit: string
  is_open: boolean
}

export interface Delivery {
  status_display: string
  id: string
  code: string
  status: 'pending' | 'delivered' | 'failed' | 'partial' | 'rejected'
  priority: 'normal' | 'urgent' | string
  priority_display?: string
  company?: string | null
  origin?: 'invoice' | 'manual'
  is_manual?: boolean
  reference?: string
  invoice?: Invoice | null
  invoice_key?: string | null
  invoice_number?: string | null
  invoice_series?: string | null
  invoice_issuer_date?: string | null
  recipient?: DeliveryRecipient | null
  issuer?: DeliveryIssuer | null
  items?: DeliveryItem[]
  pendencies?: DeliveryPendency[]
  warnings?: string[]
  delivery_address: DeliveryAddress | null
  observations?: string | null
  assigned_to?: number | null
  assigned_to_name?: string | null
  planned_delivery_at?: string | null
  created_at?: string
  updated_at?: string
  delivery_at?: string | null
  proofs?: ProofOfDelivery[]
}

export interface ProofOfDelivery {
  id: string
  delivery: string
  received_by_name: string
  received_by_email?: string
  received_by_document?: string
  status: 'delivered' | 'failed' | 'partial' | string
  status_display?: string
  observations?: string
  signed_at?: string
  signed_at_server: string
  location: { longitude: number; latitude: number } | null
  signature_image: string | null
  photos: Array<{ id: string; url: string | null; created_at?: string; meta?: Record<string, unknown> }>
  meta?: Record<string, unknown>
  created_at?: string
}

export interface DeliveryTimelineEvent {
  id: string
  attempt_id?: string | null
  event: string
  actor: number | null
  actor_name: string | null
  assigned_to?: string | number | null
  assigned_to_name?: string | null
  created_at: string
  result: string
  reason: string
}

export type PaginatedResult<T> = {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

export type UserNotification = {
  code: string
  subject: string
  message: string
  notification_type: string
  notification_type_display?: string | null
  is_read: boolean
  created_at: string
  read_at: string | null
}

export type NotificationPage = PaginatedResult<UserNotification>

export interface AccountMembership {
  id: string
  account: { id: string; name: string; status: string; timezone: string }
  role: 'manager' | 'courier'
  is_active: boolean
  can_manage_billing: boolean
  capabilities: { can_manage_billing: boolean; is_account_manager: boolean }
}

export interface MeResponse {
  user_id: string
  memberships: AccountMembership[]
}

function formatDelivery(delivery: Delivery): Delivery {
  return {
    ...delivery,
    invoice: delivery.invoice ? {
      ...delivery.invoice,
      company_cnpj: formatCnpj(delivery.invoice.company_cnpj),
      recipient_document: formatDocument(delivery.invoice.recipient_document),
    } : null,
    recipient: delivery.recipient ? { ...delivery.recipient, document: formatDocument(delivery.recipient.document) } : delivery.recipient,
  }
}

export function deliveryInvoiceLabel(delivery: Delivery) {
  const number = delivery.invoice_number || delivery.invoice?.number
  const series = delivery.invoice_series || delivery.invoice?.series
  return number ? `NF ${number}${series ? `/${series}` : ''}` : 'Sem nota fiscal'
}

export function deliveryLabel(delivery: Delivery) {
  return delivery.code ? `Entrega ${delivery.code}` : deliveryInvoiceLabel(delivery)
}

export function deliveryRecipientName(delivery: Delivery) {
  return delivery.recipient?.name || delivery.invoice?.recipient_name || 'Destinatário não informado'
}

export function deliveryRecipientDocument(delivery: Delivery) {
  return delivery.recipient?.document || delivery.invoice?.recipient_document || ''
}

export async function fetchMe(): Promise<MeResponse> {
  const { data } = await api.get<MeResponse>('/v1/accounts/me/')
  return data
}

export async function fetchDeliveries(
  pageUrl?: string,
  filters?: { search?: string; status?: string; status_group?: string; company_id?: string; period_start?: string; period_end?: string; ordering?: string },
): Promise<PaginatedResult<Delivery>> {
  if (pageUrl) {
    const allowedOrigin = new URL(import.meta.env.VITE_API_BASE_URL || window.location.origin, window.location.origin).origin
    if (new URL(pageUrl, allowedOrigin).origin !== allowedOrigin) {
      throw new Error('A API retornou um endereço de paginação inválido.')
    }
  }
  const { data } = await api.get<Delivery[] | PaginatedResult<Delivery>>(pageUrl ?? endpoints.delivery.list(), {
    params: pageUrl ? undefined : Object.fromEntries(Object.entries(filters ?? {}).filter(([, value]) => Boolean(value))),
  })
  if (Array.isArray(data)) {
    return { count: data.length, next: null, previous: null, results: data.map(formatDelivery) }
  }
  if (data && typeof data.count === 'number' && Array.isArray(data.results)) {
    return { ...data, results: data.results.map(formatDelivery) }
  }
  throw new Error('A resposta da API de entregas não corresponde ao formato esperado.')
}

export async function fetchDeliveryById(id: string): Promise<Delivery> {
  const { data } = await api.get<Delivery>(endpoints.delivery.detail(id))
  return formatDelivery(data)
}

export async function fetchDeliveryProofs(deliveryId: string): Promise<PaginatedResult<ProofOfDelivery>> {
  const { data } = await api.get<ProofOfDelivery[] | PaginatedResult<ProofOfDelivery>>(endpoints.delivery.proofs(deliveryId))
  return Array.isArray(data) ? { count: data.length, next: null, previous: null, results: data } : data
}

export async function fetchDeliveryTimeline(deliveryId: string): Promise<PaginatedResult<DeliveryTimelineEvent>> {
  const { data } = await api.get<DeliveryTimelineEvent[] | PaginatedResult<DeliveryTimelineEvent>>(endpoints.delivery.timeline(deliveryId))
  return Array.isArray(data) ? { count: data.length, next: null, previous: null, results: data } : data
}

export async function uploadPodMultipart(deliveryId: string, form: FormData) {
  if (!form.has('delivery')) form.append('delivery', deliveryId)
  const { data } = await api.post(endpoints.delivery.attempts(deliveryId), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export type UpdateUserPreferencesPayload = {
  cellphone?: string | null
  allow_delivery_notifications: boolean
  allow_email_notifications: boolean
  allow_push_notifications: boolean
  allow_sms_notifications: boolean
  allow_whatsapp_notifications: boolean
  preferred_language: string
}

export async function updateUserPreferences(userId: number | string, payload: UpdateUserPreferencesPayload) {
  const { data } = await api.patch(endpoints.user.preferences(userId), payload)
  return data
}

export type ChangePasswordPayload = {
  current_password: string
  new_password: string
}

export async function changePassword(payload: ChangePasswordPayload) {
  const { data } = await api.post(endpoints.user.changePassword(), payload)
  return data
}

export async function fetchNotifications(pageUrl?: string): Promise<NotificationPage> {
  const { data } = await api.get<NotificationPage>(pageUrl ?? endpoints.user.notifications(), {
    params: pageUrl ? undefined : { limit: 10, offset: 0 },
  })
  return data
}

export async function fetchUnreadNotificationCount(): Promise<number> {
  const { data } = await api.get<NotificationPage>(endpoints.user.notifications(), {
    params: { limit: 1, offset: 0, is_read: false },
  })
  return data.count
}

export async function markAllNotificationsRead(): Promise<void> {
  await api.post(`${endpoints.user.notifications()}mark_all/`, { is_read: true })
}
